import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { meetingTypes } from '../../components/RecordPresentation/meetingTypes';
import DocumentUploadModal from '../../components/RecordPresentation/DocumentUploadModal';
import ProspectPrepLoadingModal from '../../components/RecordPresentation/ProspectPrepLoadingModal';
import {
  PageWrapper, PageHeader, PageTitle, PageSubtitle,
  SectionHeader, CardGrid, SessionCard, CardIcon, CardTitleRow, CardTitle,
  InfoIconWrapper, InfoIcon, InfoTooltip,
  LevelBadge, StartButton, ErrorBanner,
} from './TrainingHubPage.styles';

const LEVEL_CONFIG = {
  1: { label: 'Level 1', accent: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', badgeBg: 'rgba(59, 130, 246, 0.1)' },
  2: { label: 'Level 2', accent: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', badgeBg: 'rgba(16, 185, 129, 0.1)' },
  3: { label: 'Level 3', accent: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', badgeBg: 'rgba(245, 158, 11, 0.1)' },
  4: { label: 'Level 4', accent: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', badgeBg: 'rgba(139, 92, 246, 0.1)' },
};

const ICON_MAP = {
  elevator_pitch: '\uD83D\uDCDE',
  elevator_pitch_2: '\uD83D\uDCDE',
  casual_encounter: '\uD83D\uDC65',
  casual_encounter_2: '\uD83D\uDC65',
  sales_presentation: '\uD83D\uDDA5\uFE0F',
  sales_presentation_2: '\uD83D\uDDA5\uFE0F',
  qa_practice: '\u2753',
  prospect_prep: '\uD83D\uDD0D',
  deal_coaching: '\uD83D\uDCC4',
  investor_pitch: '\uD83D\uDCBC',
};

const TrainingHubPage = observer(({ meetingStore, companyStore }) => {
  const history = useHistory();
  const [error, setError] = useState('');
  const [loadingKey, setLoadingKey] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  const onRecordingTypeClick = async (recordingKey) => {
    setError('');
    setLoadingKey(recordingKey);
    try {
      if (recordingKey === 'qa_practice') {
        await meetingStore.initQASession(companyStore.company.id, 5);
        history.push('/record-presentation/objection-handling');
      } else if (recordingKey === 'prospect_prep') {
        await meetingStore.initProspectPrepSession(companyStore.company.id, 5);
        history.push('/record-presentation/messaging-defense');
      } else if (recordingKey === 'deal_coaching') {
        setLoadingKey(null);
        setShowDocumentModal(true);
      } else {
        await meetingStore.setMeetingType(recordingKey, companyStore.company.id);
        history.push(`/record-presentation/${recordingKey}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to start session');
      setLoadingKey(null);
    }
  };

  const handleDocumentQA = async (file) => {
    const companyId = companyStore.company.id;
    const filename = `${Date.now()}-${file.name}`;
    const contentType = file.type || 'application/octet-stream';
    const headers = { 'Content-Type': contentType };

    const uploadResult = await axios.post(
      `/companies/${companyId}/uploads/doc-qa%2F${encodeURIComponent(filename)}`,
      {},
      { headers }
    );
    const uploadUrl = uploadResult.data.uploadUrl;
    const documentKey = uploadResult.data.key;

    await axios.put(uploadUrl, file, { headers, baseURL: '' });
    await meetingStore.initDocumentQASession(companyId, 5, documentKey);
    history.push('/record-presentation/document-defense');
  };

  const level1 = meetingTypes.filter(t => t.level === 1);
  const level2 = meetingTypes.filter(t => t.level === 2);
  const level3 = meetingTypes.filter(t => t.level === 3);
  const level4 = meetingTypes.filter(t => t.level === 4);

  const renderCard = (mt, tooltipBelow) => {
    const config = LEVEL_CONFIG[mt.level];
    const icon = ICON_MAP[mt.recordingKey] || '\uD83C\uDFAF';
    const isThisLoading = loadingKey === mt.recordingKey;
    const anyLoading = loadingKey !== null;

    return (
      <SessionCard key={mt.recordingKey} $accent={config.accent}>
        <CardIcon $bg={config.bg}>{icon}</CardIcon>
        <CardTitleRow>
          <CardTitle>{mt.type}</CardTitle>
          <InfoIconWrapper>
            <InfoIcon>i</InfoIcon>
            <InfoTooltip $below={tooltipBelow}>{mt.description}</InfoTooltip>
          </InfoIconWrapper>
        </CardTitleRow>
        <LevelBadge $color={config.accent} $bg={config.badgeBg}>
          {config.label}{mt.interactions ? ' \u2022 Interactive' : ''}
        </LevelBadge>
        <StartButton
          disabled={mt.disabled || anyLoading}
          onClick={() => onRecordingTypeClick(mt.recordingKey)}
        >
          {isThisLoading ? 'Loading...' : mt.disabled ? 'Coming Soon' : '\uD83C\uDFA5 Start Recording'}
        </StartButton>
      </SessionCard>
    );
  };

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Training Hub</PageTitle>
        <PageSubtitle>
          Choose the type of presentation to get started. Make sure you are prepared and facing the camera.
        </PageSubtitle>
      </PageHeader>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <SectionHeader>Pitch Training</SectionHeader>
      <CardGrid>{level1.map(mt => renderCard(mt, true))}</CardGrid>

      <SectionHeader>Interactive Pitch Training</SectionHeader>
      <CardGrid>{level2.map(renderCard)}</CardGrid>

      <SectionHeader>Interactive Defensive Training</SectionHeader>
      <CardGrid>{level3.map(renderCard)}</CardGrid>

      {companyStore.company.investorPitchEnabled && (
        <>
          <SectionHeader>Investor Pitch Training</SectionHeader>
          <CardGrid>{level4.map(renderCard)}</CardGrid>
        </>
      )}

      <DocumentUploadModal
        open={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        onDocumentQA={handleDocumentQA}
      />

      <ProspectPrepLoadingModal open={loadingKey === 'prospect_prep'} />
    </PageWrapper>
  );
});

export default TrainingHubPage;
